/**
 * Queued Attachments
 *
 * Preserves draft attachments across delayed chat sends.
 *
 * Primary responsibilities:
 * - Clone attachment snapshots before queueing
 * - Merge scoped attachment stores without duplicate entries
 * - Remove accepted draft attachments from composer state
 *
 * Dependencies:
 * - Chat attachment types
 * - Scoped attachment bindings
 */

import type { ScopedAttachmentBindings } from "@/context/context"
import type { ChatFile } from "@/types/chat-file"
import type { ChatQueuedAttachments } from "@/types/chat"
import type { MessageImage } from "@/types/images/message-image"

const getImageKey = (image: MessageImage) =>
  image.messageId || image.path || image.url || String(image.base64 || "")

const getFileKey = (file: ChatFile) => file.id || file.name

function cloneImages(images: MessageImage[]) {
  return images.map(image => ({ ...image }))
}

function cloneFiles(files: ChatFile[]) {
  return files.map(file => ({ ...file }))
}

function mergeUniqueByKey<T>(
  existing: T[],
  incoming: T[],
  getKey: (item: T) => string
) {
  const seenKeys = new Set(existing.map(getKey).filter(Boolean))
  const nextItems = [...existing]

  for (const item of incoming) {
    const key = getKey(item)
    if (key && seenKeys.has(key)) continue
    if (key) {
      seenKeys.add(key)
    }
    nextItems.push(item)
  }

  return nextItems
}

export function cloneQueuedAttachments(
  attachments: ChatQueuedAttachments
): ChatQueuedAttachments {
  return {
    chatFiles: cloneFiles(attachments.chatFiles),
    chatImages: cloneImages(attachments.chatImages),
    newMessageFiles: cloneFiles(attachments.newMessageFiles),
    newMessageImages: cloneImages(attachments.newMessageImages),
    showFilesDisplay: attachments.showFilesDisplay
  }
}

export function mergeQueuedAttachments(
  ...snapshots: Array<ChatQueuedAttachments | null | undefined>
): ChatQueuedAttachments {
  return snapshots.reduce<ChatQueuedAttachments>(
    (merged, snapshot) => {
      if (!snapshot) return merged
      const cloned = cloneQueuedAttachments(snapshot)

      return {
        chatFiles: mergeUniqueByKey(
          merged.chatFiles,
          cloned.chatFiles,
          getFileKey
        ),
        chatImages: mergeUniqueByKey(
          merged.chatImages,
          cloned.chatImages,
          getImageKey
        ),
        newMessageFiles: mergeUniqueByKey(
          merged.newMessageFiles,
          cloned.newMessageFiles,
          getFileKey
        ),
        newMessageImages: mergeUniqueByKey(
          merged.newMessageImages,
          cloned.newMessageImages,
          getImageKey
        ),
        showFilesDisplay: merged.showFilesDisplay || cloned.showFilesDisplay
      }
    },
    {
      chatFiles: [],
      chatImages: [],
      newMessageFiles: [],
      newMessageImages: [],
      showFilesDisplay: false
    }
  )
}

export function hasQueuedDraftAttachments(
  attachments?: ChatQueuedAttachments | null
) {
  return Boolean(
    attachments &&
      (attachments.newMessageFiles.length > 0 ||
        attachments.newMessageImages.length > 0)
  )
}

export function removeQueuedDraftAttachments(
  bindings: ScopedAttachmentBindings,
  attachments: ChatQueuedAttachments
) {
  const imageKeys = new Set(
    attachments.newMessageImages.map(getImageKey).filter(Boolean)
  )
  const fileKeys = new Set(
    attachments.newMessageFiles.map(getFileKey).filter(Boolean)
  )

  const snapshot = bindings.getSnapshot()
  const remainingImages = snapshot.newMessageImages.filter(
    image => !imageKeys.has(getImageKey(image))
  )
  const remainingFiles = snapshot.newMessageFiles.filter(
    file => !fileKeys.has(getFileKey(file))
  )

  if (imageKeys.size > 0) {
    bindings.setNewMessageImages(remainingImages)
  }

  if (fileKeys.size > 0) {
    bindings.setNewMessageFiles(remainingFiles)
  }

  if (imageKeys.size > 0 || fileKeys.size > 0) {
    bindings.setShowFilesDisplay(remainingImages.length > 0 || remainingFiles.length > 0)
  }
}
